import React, { useState } from "react";
import styles from "./ConfirmationModal.module.scss";
import Button from "../../../../../common/components/atom/Button/Button";
import TextInput from "../../../../../common/components/atom/TextInput/TextInput";
import { WalletCurrency } from "../../../types/wallet.types";
import { numberFormaterString } from "../../../../../utils/numberFormater";

export interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  amount: number;
  currency: WalletCurrency;
  isLoading?: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  amount,
  currency,
  isLoading = false,
}: ConfirmationModalProps) {
  const [confirmationText, setConfirmationText] = useState("");
  const [confirmationError, setConfirmationError] = useState("");
  
  // Générer la phrase de confirmation attendue
  const expectedConfirmation = `ajouter-${amount}-${currency.toLowerCase()}`;
  
  if (!isOpen) return null;

  const validateConfirmation = () => {
    if (confirmationText.toLowerCase().trim() !== expectedConfirmation) {
      setConfirmationError(`Veuillez taper exactement : ${expectedConfirmation}`);
      return false;
    }
    setConfirmationError("");
    return true;
  };

  const handleConfirm = () => {
    if (validateConfirmation()) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    setConfirmationText("");
    setConfirmationError("");
    onCancel();
  };

  return (
    <div className={styles["modal-overlay"]}>
      <div className={styles["modal-content"]}>
        <div className={styles["modal-header"]}>
          <h2 className={styles["modal-title"]}>
            Confirmation d'ajout de crédit manuel
          </h2>
        </div>
        
        <div className={styles["modal-body"]}>
          <div className={styles["warning-section"]}>
            <div className={styles["warning-icon"]}>⚠️</div>
            <div className={styles["warning-content"]}>
              <h3 className={styles["warning-title"]}>
                Attention : Modification du crédit en circulation
              </h3>
              <p className={styles["warning-description"]}>
                Cette opération va ajouter <strong>{numberFormaterString(amount)} {currency}</strong> au 
                portefeuille de l'utilisateur. Cette action impacte directement le montant total 
                de crédit en circulation dans le système.
              </p>
            </div>
          </div>

          <div className={styles["info-section"]}>
            <h4 className={styles["info-title"]}>Implications de cette action :</h4>
            <ul className={styles["info-list"]}>
              <li>Le crédit sera immédiatement disponible sur le portefeuille</li>
              <li>Cette transaction sera enregistrée dans l'historique</li>
              <li>Le montant global de crédit en circulation sera augmenté</li>
              <li>Cette opération est irréversible</li>
            </ul>
          </div>

          <div className={styles["confirmation-section"]}>
            <div className={styles["confirmation-instruction"]}>
              <h4 className={styles["confirmation-title"]}>
                Confirmation de sécurité requise
              </h4>
              <p className={styles["confirmation-description"]}>
                Pour confirmer cette opération, veuillez taper exactement la phrase suivante :
              </p>
              <div className={styles["expected-phrase"]}>
                <code>{expectedConfirmation}</code>
              </div>
            </div>
            
            <div className={styles["confirmation-input"]}>
              <TextInput
                label="Phrase de confirmation"
                value={confirmationText}
                onChange={(e) => {
                  setConfirmationText(e.target.value);
                  if (confirmationError) {
                    setConfirmationError("");
                  }
                }}
                placeholder={expectedConfirmation}
                error={!!confirmationError}
                disabled={isLoading}
              />
              {confirmationError && (
                <div className={styles["confirmation-error"]}>
                  {confirmationError}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles["modal-footer"]}>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={isLoading || !confirmationText.trim()}
          >
            Confirmer l'ajout
          </Button>
        </div>
      </div>
    </div>
  );
}
